// pages/Notification.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { APP_CONFIG } from "../app-config";
import BannerCarousel from "../components/banner-carousel";
import SidebarHeader from "../components/SidebarHeader";
import BottomNav from "../components/bottom-menu";
import toast, { Toaster } from 'react-hot-toast';
import { apiCall } from "../utils/api";
import Bmnemonic_PNG from "../assets/image/Bmnemonic_PNG.png"
import { FiArrowLeft } from "react-icons/fi";

const COLORS = APP_CONFIG.colors;

const iconMap = {
  offer: (color) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 12 20 22 4 22 4 12" /><rect x="2" y="7" width="20" height="5" /><line x1="12" y1="22" x2="12" y2="7" /><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" /><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
    </svg>
  ),
  update: (color) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  alert: (color) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
};

// Dummy notifications data
const dummyNotifications = [
  {
    id: 1,
    type: 'offer',
    title: '🎉 Special Offer!',
    message: 'Get 10% extra cashback on all recharges this weekend.',
    time: '2 min ago',
    unread: true
  },
  {
    id: 2,
    type: 'update',
    title: '📱 App Update',
    message: 'We\'ve improved your experience with faster loading times.',
    time: '1 hour ago',
    unread: true
  },
  {
    id: 3,
    type: 'alert',
    title: '⚠️ Payment Alert',
    message: 'Your recent recharge of ₹199 was successful. Transaction ID: TXN12345',
    time: '3 hours ago',
    unread: true
  },
  {
    id: 4,
    type: 'offer',
    title: '🎁 Cashback Offer',
    message: 'Get ₹50 cashback on first DTH recharge of the month.',
    time: '5 hours ago',
    unread: false
  },
  {
    id: 5,
    type: 'update',
    title: '🔄 Service Update',
    message: 'We have added new operators for mobile recharge in your region.',
    time: '1 day ago',
    unread: false
  }
];

function NotificationItem({ notif, onDelete, onArchive }) {
  const [swipeX, setSwipeX] = useState(0);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);

  const onStart = (e) => {
    setStartX(e.type.includes("touch") ? e.touches[0].clientX : e.clientX);
    setStartY(e.type.includes("touch") ? e.touches[0].clientY : e.clientY);
    setIsSwiping(true);
  };

  const onMove = (e) => {
    if (!isSwiping) return;
    const currentX = e.type.includes("touch") ? e.touches[0].clientX : e.clientX;
    const currentY = e.type.includes("touch") ? e.touches[0].clientY : e.clientY;
    const diffX = currentX - startX;
    const diffY = currentY - startY;

    if (Math.abs(diffY) > Math.abs(diffX)) {
      setIsSwiping(false);
      setSwipeX(0);
      return;
    }

    if (Math.abs(diffX) < 150) {
      setSwipeX(diffX);
    }
  };

  const onEnd = () => {
    setIsSwiping(false);
    if (swipeX > 100) {
      onArchive(notif.id);
    } else if (swipeX < -100) {
      onDelete(notif.id);
    } else {
      setSwipeX(0);
    }
  };

  return (
    <div
      className="relative overflow-hidden rounded-xl"
      style={{
        touchAction: "pan-y",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}
      onTouchStart={onStart}
      onTouchMove={onMove}
      onTouchEnd={onEnd}
      onMouseDown={onStart}
      onMouseMove={onMove}
      onMouseUp={onEnd}
      onMouseLeave={onEnd}
    >
      {/* Background Actions */}
      <div className="absolute inset-0 flex items-center justify-end rounded-xl" style={{ background: COLORS.errorColor }}>
        {swipeX > 0 && (
          <div className="w-full flex justify-start px-6 text-white text-xs font-bold">
            ARCHIVE
          </div>
        )}
        {swipeX < 0 && (
          <div className="w-full flex justify-end px-6 text-white text-xs font-bold">
            DELETE
          </div>
        )}
      </div>

      {/* Content */}
      <div
        className="flex gap-3.5 p-4 relative z-10 rounded-xl"
        style={{
          background: COLORS.bgColor,
          border: `1px solid ${COLORS.borderColor}`,
          transform: `translateX(${swipeX}px)`,
          transition: isSwiping ? "none" : "transform 0.3s ease",
        }}
      >
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{
            background: notif.type === "alert" ? `${COLORS.errorColor}15` : `${COLORS.primaryColor}15`,
          }}
        >
          {iconMap[notif.type](notif.type === "alert" ? COLORS.errorColor : COLORS.primaryColor)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start mb-1">
            <h4
              className="text-sm m-0"
              style={{
                fontWeight: notif.unread ? 800 : 600,
                color: notif.unread ? COLORS.textPrimary : COLORS.textSecondary,
              }}
            >
              {notif.title}
            </h4>
            <span
              className="text-[11px] flex-shrink-0 ml-2"
              style={{ color: COLORS.textSecondary }}
            >
              {notif.time}
            </span>
          </div>
          <p
            className="text-[13px] leading-relaxed m-0"
            style={{
              color: COLORS.textSecondary,
              opacity: notif.unread ? 1 : 0.7,
            }}
          >
            {notif.message}
          </p>
        </div>
        {notif.unread && (
          <div
            className="absolute top-4 right-3 w-2 h-2 rounded-full"
            style={{
              background: COLORS.primaryColor,
              boxShadow: `0 0 8px ${COLORS.primaryColor}80`,
            }}
          />
        )}
      </div>
    </div>
  );
}

function Notification() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState(dummyNotifications);

  const markAllNotificationsRead = () => {
    setNotifications(prev =>
      prev.map(notif => ({ ...notif, unread: false }))
    );
  };

  const deleteNotification = (id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  };

  const archiveNotification = (id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      markAllNotificationsRead();
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const unreadCount = notifications.filter(n => n.unread).length;

  return (
    <SidebarHeader>
      <div className="flex items-center gap-3 pb-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-200 hover:bg-gray-300 transition"
        >
          <FiArrowLeft className="text-xl" />
        </button>
        <h1 className="text-lg font-semibold text-gray-800">
          Notification
        </h1>
      </div>
      <div className="max-w-7xl mx-auto mb-16">
        <div className="rounded-2xl p-4 md:p-6"
          style={{
            background: COLORS.bgColor,
            boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
            border: `1px solid ${COLORS.borderColor}`,
          }}>

          {/* Header with unread count */}
          {notifications.length > 0 && (
            <div className="flex justify-between items-center mb-4 pb-2 border-b" style={{ borderBottomColor: COLORS.borderColor }}>
              <div>
                <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>
                  Notifications
                </span>
                {unreadCount > 0 && (
                  <span
                    className="ml-2 text-xs px-2 py-0.5 rounded-full"
                    style={{
                      background: COLORS.primaryColor,
                      color: COLORS.textOnPrimary,
                    }}
                  >
                    {unreadCount} new
                  </span>
                )}
              </div>
              {unreadCount > 0 && (
                <button
                  onClick={markAllNotificationsRead}
                  className="text-xs font-medium hover:opacity-70 transition-opacity"
                  style={{ color: COLORS.primaryColor }}
                >
                  Mark all as read
                </button>
              )}
            </div>
          )}

          {notifications.length === 0 ? (
            <div className="text-center py-20">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ background: COLORS.surfaceColor }}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={COLORS.textSecondary} strokeWidth="1.5" strokeLinecap="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
              </div>
              <p className="text-base font-semibold mb-1" style={{ color: COLORS.textPrimary }}>
                No new notifications
              </p>
              <p className="text-[13px] mt-1" style={{ color: COLORS.textSecondary }}>
                We'll notify you when something important happens.
              </p>
            </div>
          ) : (
            <div className="pt-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {notifications.map((notif) => (
                  <NotificationItem
                    key={notif.id}
                    notif={notif}
                    onDelete={deleteNotification}
                    onArchive={archiveNotification}
                  />
                ))}
              </div>

              <p
                className="text-[11px] text-center mt-5 opacity-60"
                style={{ color: COLORS.textSecondary }}
              >
                Swipe left to delete or right to archive
              </p>
            </div>
          )}
        </div>
      </div>

      <Toaster />

      {/* Bottom Navigation - Hidden on desktop (lg and above) */}
      <div className="lg:hidden">
        <BottomNav />
      </div>

      <style>
        {`
          .duration-300 {
            transition-duration: 300ms;
          }
        `}
      </style>
    </SidebarHeader>
  );
}

export default Notification;